import Vue from "vue";
import App from "./components/App.vue";
import VueRouter from "vue-router";
import VueApollo from "vue-apollo";
import ToastPlugin from "vue-toast-notification";
import { appsyncClient, djangoClient } from "./api/clients";
import "./styles/main.scss";
import "vue-toast-notification/dist/theme-sugar.css";
import { createRouter } from "./router";
import PageLoading from "./components/ui/PageLoading.vue";
import Loading from "../common/components/Loading.vue";
import JcButton from "../common/components/JcButton.vue";
import formatMoney from "./utils/formatMoney";
import formatPercentage from "./utils/formatPercentage";
import formatBytes from "../common/utils/formatBytes";
import VModal from "vue-js-modal";
import VueCompositionAPI from "@vue/composition-api";

import "vue-progress-path/dist/vue-progress-path.css";
import VueProgress from "vue-progress-path";
import Vuelidate from "vuelidate";
import VueClipboard from "vue-clipboard2";
import VTooltip from "v-tooltip";

import * as Sentry from "@sentry/browser";
import { Vue as VueIntegration } from "@sentry/integrations";

Vue.use(VueCompositionAPI);
Vue.use(Vuelidate);
Vue.use(VueProgress);
Vue.use(VueRouter);
Vue.use(VueApollo);
Vue.use(VueClipboard);
Vue.use(VModal);
Vue.use(VTooltip);
Vue.use(ToastPlugin);

Vue.component("page-loading", PageLoading);
Vue.component("loading", Loading);
Vue.component("jc-button", JcButton);

Vue.filter("money", formatMoney);
Vue.filter("percentage", formatPercentage);
Vue.filter("bytes", formatBytes);

Vue.directive("appear", {
  bind(el, binding) {
    // Create a mutation observer to watch for changes in the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          // Check if the element is visible
          if (
            el.style.display !== "none" &&
            el.style.visibility !== "hidden" &&
            !el.hidden
          ) {
            // Call the callback function provided in the directive
            binding.value();
            // Once the callback is called, disconnect the observer
            observer.disconnect();
          }
        }
      });
    });

    // Start observing the element
    observer.observe(el, { attributes: true });
  },
});

Sentry.init({
  dsn: "https://f04e987460a44390a4fbecbe620a8bbc@o34027.ingest.sentry.io/5211808",
  integrations: [new VueIntegration({ Vue, attachProps: true })],
});

export default ({ helpCrunch }) => {
  if (process.env.NODE_ENV === "development") {
    console.info("Help Crunch config", helpCrunch);
  }

  // Phase B.6: default client is now AppSync for card/contribution data ops.
  // Auth-only queries (e.g. `viewer`) opt in to the named `django` client via
  // `client: "django"` on the `apollo` option. The video-flow ops in the
  // videoRecorder machine talk to `appsyncClient` directly by reference and
  // are unaffected by the default.
  const apolloProvider = new VueApollo({
      defaultClient: appsyncClient,
      clients: { django: djangoClient },
      errorHandler(error) {
        // Recognise auth-error shapes from both backends:
        //   Django Graphene → HTTP 200 with graphQLErrors[].message
        //     "Permission Denied"
        //   AppSync Lambda authorizer → HTTP 401 with body
        //     `{ errors: [{ errorType: "UnauthorizedException", ... }] }`.
        //     apollo-link-http surfaces 401s on `error.networkError` (NOT
        //     graphQLErrors) — `statusCode` is on the ServerError and the
        //     parsed body lives at `error.networkError.result`.
        const message = error.message || "";
        const isDjangoPermissionDenied = message.includes("Permission Denied");
        const isAppsyncUnauthorized =
          message.includes("not authorized") ||
          error.graphQLErrors?.some(
            (e) => e.errorType === "UnauthorizedException",
          ) ||
          error.networkError?.statusCode === 401 ||
          error.networkError?.result?.errors?.some(
            (e) => e.errorType === "UnauthorizedException",
          );
        if (!(isDjangoPermissionDenied || isAppsyncUnauthorized)) {
          return;
        }
        // B.6b: the contributor-invite, save-card, and recipient-redirect
        // routes are entry points that explicitly handle the unauth case
        // themselves (landing UI / Django bounce). Don't yank the user
        // away to /accounts/login/ when the viewer query 403s on those
        // pages — that would skip the landing UX entirely.
        const path = window.location.pathname;
        const isPublicEntryPoint =
          path.startsWith("/cards/contribute/") ||
          path.startsWith("/cards/save/") ||
          /^\/cards\/[^/]+\/?$/.test(path);
        if (isPublicEntryPoint) {
          return;
        }
        window.location.replace("/accounts/login/");
      },
    }),
    router = createRouter();

  return new Vue({
    router,
    apolloProvider,
    components: { App },
    data: { helpCrunch },
    template: "<app :help-crunch='this.helpCrunch' />",
  });
};
